import React, {useEffect, useState} from 'react';
import smsgatwayservice from "../../../services/smsgateway.service";
import Loading from "../../common/Loading";

const AdminSmsGatewayDashboard = () => {
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                console.log("Fetching SMS gateway stats...");

                const response = await smsgatwayservice.getAdminSmsGatewayStats();
                console.log("API Response:", response);

                // Check if response exists and has data
                if (response && Array.isArray(response.data)) {
                    setStats(response.data);
                    console.log("Stats data set:", response.data);
                } else if (response && response.data) {
                    // Handle case where data might not be an array
                    setStats([response.data]);
                } else {
                    setStats([]);
                    setError("Invalid response format from server");
                }
            } catch (err) {
                console.error("Error fetching SMS gateway stats:", err);
                console.error("Error details:", err.response || err.message);

                // More specific error messages
                if (err.response) {
                    setError(`Server error: ${err.response.status} - ${err.response.statusText}`);
                } else if (err.request) {
                    setError("Network error: Could not connect to server");
                } else {
                    setError(`Error: ${err.message}`);
                }
                setStats([]);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    // Add a refresh function
    const refreshData = () => {
        setError(null);
        setLoading(true);
        const fetchStats = async () => {
            try {
                const response = await smsgatwayservice.getAdminSmsGatewayStats();
                if (response && Array.isArray(response?.data)) {
                    setStats(response.data);
                } else {
                    setError("Invalid data format received");
                }
            } catch (err) {
                console.error("Error refreshing data:", err);
                setError("Failed to refresh data");
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    };

    // Categorize SMS gateway statistics
    const getCategoryStats = (category) => {
        if (!Array.isArray(stats)) return [];

        return stats.filter(stat => {
            if (!stat?.statsName) return false;
            const name = stat.statsName.toLowerCase();

            switch (category) {
                case 'delivery':
                    return name.includes('delivery') || name.includes('delivered') ||
                        name.includes('sent') || name.includes('failed');
                case 'incoming':
                    return name.includes('incoming') || name.includes('received') ||
                        name.includes('read');
                case 'outgoing':
                    return name.includes('outgoing') || name.includes('sent') ||
                        name.includes('queued') || name.includes('processed');
                case 'rates':
                    return name.includes('rate') || name.includes('percentage');
                case 'sources':
                    return name.includes('eyec') || name.includes('pms');
                default:
                    return false;
            }
        });
    };

    // Get icon for individual stat cards
    const getStatIcon = (statName) => {
        if (!statName) return 'bi-graph-up';
        if (statName.includes('DELIVERY') || statName.includes('DELIVERED')) return 'bi-check-circle';
        if (statName.includes('FAILED')) return 'bi-exclamation-triangle';
        if (statName.includes('SENT')) return 'bi-send-check';
        if (statName.includes('QUEUED')) return 'bi-hourglass-split';
        if (statName.includes('PROCESSED')) return 'bi-gear';
        if (statName.includes('READ')) return 'bi-eye';
        if (statName.includes('UNREAD')) return 'bi-eye-slash';
        if (statName.includes('RATE') || statName.includes('PERCENTAGE')) return 'bi-percent';
        if (statName.includes('INCOMING')) return 'bi-inbox';
        if (statName.includes('OUTGOING')) return 'bi-send';
        if (statName.includes('RECEIVED')) return 'bi-download';
        return 'bi-graph-up';
    };

    // Format display names
    const formatName = (name) => {
        if (!name) return '';
        return name
            .replace(/_/g, ' ')
            .replace(/(^\w|\s\w)/g, m => m.toUpperCase());
    };

    if (loading) {
        return <Loading/>;
    }

    if (error) {
        return (
            <div className="container py-4">
                <div className="alert alert-danger">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    <strong>Error:</strong> {error}
                </div>
                <div className="text-center mt-4">
                    <button
                        className="btn btn-primary"
                        onClick={refreshData}
                    >
                        <i className="bi bi-arrow-clockwise me-2"></i>
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (!stats.length) {
        return (
            <div className="text-center py-5">
                <i className="bi bi-database-exclamation fs-1 text-muted"></i>
                <h4 className="mt-3">No SMS gateway data available</h4>
                <button
                    className="btn btn-primary mt-3"
                    onClick={refreshData}
                >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Refresh Data
                </button>
            </div>
        );
    }

    return (
        <div className="container py-4">
            {/* Dashboard Header */}
            <div className="d-flex align-items-center mb-4">
                <div className="bg-primary bg-opacity-10 p-3 rounded me-3">
                    <i className="bi bi-chat-dots fs-2 text-primary"></i>
                </div>
                <div>
                    <h4 className="mb-0">SMS Gateway Dashboard</h4>
                    <p className="text-muted mb-0">Overview of SMS messaging statistics and performance</p>
                </div>
            </div>

            {/* Overview Cards */}
            <div className="row mb-5 g-4">
                {[
                    {
                        title: 'Total Outgoing',
                        key: 'TOTAL_OUTGOING',
                        icon: 'bi-send',
                        color: 'primary',
                        desc: 'All outgoing messages'
                    },
                    {
                        title: 'Total Incoming',
                        key: 'TOTAL_INCOMING',
                        icon: 'bi-inbox',
                        color: 'info',
                        desc: 'All incoming messages'
                    },
                    {
                        title: 'Messages Delivered',
                        key: 'OUTGOING_DELIVERED',
                        icon: 'bi-check-circle',
                        color: 'success',
                        desc: 'Successfully delivered messages'
                    },
                    {
                        title: 'Delivery Rate',
                        key: 'ESTIMATED_DELIVERY_RATE',
                        icon: 'bi-percent',
                        color: 'warning',
                        desc: 'Estimated delivery success rate'
                    }
                ].map((metric, i) => {
                    const stat = stats.find(s => s.statsName === metric.key);
                    return (
                        <div key={i} className="col-md-6 col-lg-3">
                            <div
                                className={`card card-dashboard border-start border-${metric.color} border-4 h-100 shadow-sm`}>
                                <div className="p-2"></div>
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <i className={`bi ${metric.icon} fs-2 text-${metric.color} me-3`}></i>
                                        <div>
                                            <h5 className="text-muted mb-1"><strong>{metric.title}</strong></h5>
                                            <h4 className="mb-0">{stat?.statsValue || '0'}</h4>
                                            <small className="text-muted">{metric.desc}</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Detailed Sections */}
            {[
                {
                    title: 'Delivery Statistics',
                    category: 'delivery',
                    icon: 'bi-check-circle',
                    color: 'success',
                    desc: 'Message delivery status and performance'
                },
                {
                    title: 'Incoming Messages',
                    category: 'incoming',
                    icon: 'bi-inbox',
                    color: 'info',
                    desc: 'Received messages and read status'
                },
                {
                    title: 'Outgoing Messages',
                    category: 'outgoing',
                    icon: 'bi-send',
                    color: 'primary',
                    desc: 'Sent messages and processing status'
                },
                {
                    title: 'Performance Rates',
                    category: 'rates',
                    icon: 'bi-graph-up',
                    color: 'warning',
                    desc: 'Delivery and read rates'
                },
                {
                    title: 'Message Sources',
                    category: 'sources',
                    icon: 'bi-source',
                    color: 'secondary',
                    desc: 'Messages sent by different systems'
                }
            ].map((section, i) => {
                const sectionStats = getCategoryStats(section.category);

                return sectionStats.length > 0 && (
                    <section key={i} className="mb-5">
                        <div className="d-flex align-items-center mb-4">
                            <div className={`bg-${section.color} bg-opacity-10 p-3 rounded me-3`}>
                                <i className={`bi ${section.icon} fs-2 text-${section.color}`}></i>
                            </div>
                            <div>
                                <h4 className="mb-1">{section.title}</h4>
                                <p className="text-muted mb-0">{section.desc}</p>
                            </div>
                        </div>

                        <div className="row g-4">
                            {sectionStats.map((stat, j) => (
                                <div key={j} className="col-sm-6 col-md-4 col-lg-3">
                                    <div className="card card-dashboard h-100 shadow-sm">
                                        <div className="card-body text-center py-3">
                                            <div className="card-title text-muted mb-2">
                                                <i className={`bi ${getStatIcon(stat.statsName)} mb-2 text-${section.color}`}></i> {formatName(stat.statsName)}
                                            </div>
                                            <hr/>
                                            <h4 className={`card-text fw-bold ${
                                                stat.statsValue === '0' || stat.statsValue === '0.00' ?
                                                    'text-muted' : `text-${section.color}`
                                            }`}>
                                                {stat.statsValue}
                                            </h4>
                                        </div>
                                        <div className="card-footer bg-transparent border-top-0 pt-0 text-end">
                                            <small className="text-muted">
                                                <i className="bi bi-clock"></i> Updated: {stat.modifiedAt}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                );
            })}
        </div>
    );
};

export default AdminSmsGatewayDashboard;